import { getParam } from './automationParams.js';
import { getEffectivePrimaryEntity } from '../metadata/effectiveEntity.js';
import { serviceTargetContract } from '../../../automation/serviceTargetContract.js';

const MAX_SUMMARY_LENGTH = 52;

export function truncateSummary(value, maxLength = MAX_SUMMARY_LENGTH) {
  const text = String(value ?? '').replace(/\s+/g, ' ').trim();
  return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text;
}

function valueText(value) {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (value?.kind && typeof value.toString === 'function') return value.toString();
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export function friendlyActionName(action, serviceActions = []) {
  if (!action) return 'Not configured';
  const service = action.provider && action.service
    ? `${action.provider}.${action.service}`
    : action.service || action.provider || action.type || 'Not configured';
  const option = serviceActions.find(item => item.value === service);
  return option?.name || option?.label || service;
}

export function actionSummary(model, serviceActions = []) {
  const actions = model?.actions ?? [];
  if (actions.length === 0) return 'Not configured';
  const first = friendlyActionName(actions[0], serviceActions);
  return actions.length > 1 ? `${first} +${actions.length - 1} actions` : first;
}

export function conditionSummary(model) {
  const conditions = model?.conditions ?? [];
  if (conditions.length === 0) return '—';
  if (conditions.length > 1) return `${conditions.length} conditions`;
  const condition = conditions[0];
  return truncateSummary(
    `${valueText(condition.left)} ${condition.operator ?? '=='} ${valueText(condition.right)}`,
  );
}

function explicitTarget(action) {
  const target = action?.target;
  if (target && Object.entries(target).some(([key, value]) =>
    key !== 'source' && value !== '' && value != null && (!Array.isArray(value) || value.length > 0)
  )) {
    const entityId = target.entity_id;
    if (Array.isArray(entityId)) return entityId.join(', ');
    if (entityId) return entityId;
    return Object.values(target).filter(Boolean).join(', ');
  }
  return valueText(getParam(action?.parameters ?? [], 'entity_id')?.value);
}

export function targetSummary(model, node, serviceActions = []) {
  const actions = model?.actions ?? [];
  const contract = serviceTargetContract(actions[0], serviceActions);
  if (contract.known && !contract.supportsAny) return 'Not set';
  const explicit = actions.map(explicitTarget).filter(Boolean);
  if (explicit.length > 0) {
    const unique = [...new Set(explicit)];
    return truncateSummary(unique.length > 1 ? `${unique[0]} +${unique.length - 1}` : unique[0]);
  }
  return contract.entity && getEffectivePrimaryEntity(node)
    ? 'Primary Entity'
    : 'Not set';
}

function actionDataEntries(action) {
  const entries = [];
  for (const [key, value] of Object.entries(action?.data ?? {})) {
    entries.push([key, valueText(value)]);
  }
  for (const parameter of action?.parameters ?? []) {
    if (parameter.key && parameter.key !== 'entity_id' && !(action?.data && parameter.key in action.data)) {
      entries.push([parameter.key, valueText(parameter.value)]);
    }
  }
  return entries;
}

export function actionDataSummary(model) {
  const actions = model?.actions ?? [];
  const summaries = actions.map(action => actionDataEntries(action)
    .map(([key, value]) => `${key}: ${value}`)
    .join(', ')).filter(Boolean);
  if (summaries.length === 0) return '—';
  const summary = summaries.length > 1 ? `${summaries[0]} +${summaries.length - 1}` : summaries[0];
  return truncateSummary(summary);
}

export function cloneAutomationModel(model) {
  const copy = model.constructor.fromJSON(model.toJSON());
  copy.id = crypto.randomUUID();
  copy.triggers.forEach(trigger => { trigger.id = crypto.randomUUID(); });
  copy.conditions.forEach(condition => { condition.id = crypto.randomUUID(); });
  copy.actions.forEach(action => { action.id = crypto.randomUUID(); });
  copy.bindings.forEach(binding => { binding.id = crypto.randomUUID(); });
  return copy;
}
